import React from 'react'
import { Area, AreaChart, Line, LineChart, ResponsiveContainer, YAxis } from 'recharts'
import { LazyMount } from './lazy-mount'

export type SparklineVariant = 'table' | 'tile'

type Props = {
    /** One entry per day. A `null` day had no data, and breaks the line; a `0` day dips to zero. */
    values: Array<number | null>
    variant: SparklineVariant
    /** Any CSS colour, including a `var(--ant-color-...)` reference. */
    color?: string
    /** The adjacent value and delta carry the meaning, so a sparkline is decorative unless told otherwise. */
    ariaHidden?: boolean
}

const TABLE_WIDTH = 44
const TABLE_HEIGHT = 16
const TILE_HEIGHT = 44
const CHART_MARGIN = { top: 2, bottom: 2, left: 0, right: 0 }

/** The smallest share of the series mean that the vertical axis spans, so a flat series stays visibly flat. */
const MINIMUM_VISIBLE_RANGE_SHARE_OF_MEAN = 0.04
const MINIMUM_VISIBLE_RANGE_WHEN_MEAN_IS_ZERO = 1

export const sparklineValueDomain = (values: Array<number | null>): [number, number] => {
    const present = values.filter((value): value is number => value !== null)
    if (present.length === 0) return [0, MINIMUM_VISIBLE_RANGE_WHEN_MEAN_IS_ZERO]
    const minimum = Math.min(...present)
    const maximum = Math.max(...present)
    const mean = present.reduce((total, value) => total + value, 0) / present.length
    const minimumRange =
        mean === 0 ? MINIMUM_VISIBLE_RANGE_WHEN_MEAN_IS_ZERO : Math.abs(mean) * MINIMUM_VISIBLE_RANGE_SHARE_OF_MEAN
    if (maximum - minimum >= minimumRange) return [minimum, maximum]
    const midpoint = (minimum + maximum) / 2
    return [midpoint - minimumRange / 2, midpoint + minimumRange / 2]
}

const DEFAULT_COLOR_BY_VARIANT: Record<SparklineVariant, string> = {
    table: 'var(--ant-color-text-quaternary)',
    tile: 'var(--ant-color-primary)',
}

export const Sparkline: React.FC<Props> = ({ values, variant, color, ariaHidden = true }) => {
    const stroke = color ?? DEFAULT_COLOR_BY_VARIANT[variant]
    const data = values.map((value, dayIndex) => ({ dayIndex, value }))
    const yAxis = <YAxis hide domain={sparklineValueDomain(values)} />

    if (variant === 'tile') {
        return (
            <span className="org-dashboard-sparkline org-dashboard-sparkline-tile" aria-hidden={ariaHidden}>
                <ResponsiveContainer width="100%" height={TILE_HEIGHT}>
                    <AreaChart data={data} margin={CHART_MARGIN}>
                        {yAxis}
                        <Area
                            type="monotone"
                            dataKey="value"
                            stroke={stroke}
                            strokeWidth={1.5}
                            fill={stroke}
                            fillOpacity={0.2}
                            dot={false}
                            activeDot={false}
                            connectNulls={false}
                            isAnimationActive={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </span>
        )
    }

    return (
        <span className="org-dashboard-sparkline org-dashboard-sparkline-table" aria-hidden={ariaHidden}>
            <LazyMount placeholderWidth={TABLE_WIDTH} placeholderHeight={TABLE_HEIGHT}>
                <LineChart width={TABLE_WIDTH} height={TABLE_HEIGHT} data={data} margin={CHART_MARGIN}>
                    {yAxis}
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke={stroke}
                        strokeWidth={1.25}
                        dot={false}
                        activeDot={false}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </LazyMount>
        </span>
    )
}
